import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PageHead, VegetarianMark } from "../components/site/Blocks";
import { getSeason } from "../config/hours";
import { CONTACT } from "../config/location";

// Tabs = Menü-Namen der API. PDF-Dateien liegen unter public/documents.
const TABS = [
    { name: "Speisekarte", pdf: "Speisekarte.pdf" },
    { name: "Mittagskarte", pdf: "Mittagskarte.pdf" },
    { name: "Weinkarte", pdf: "Weinkarte.pdf" },
    { name: "Bestellkarte" },
];

const SIDE_DISH_NOTE =
    "Alle Gerichte werden mit Salat und Beilage nach Wunsch serviert. Beilagen: Pommes, Reis, Kroketten, Kartoffelchips, Bandnudeln.";

const tabIntro = (name) => {
    const season = getSeason();
    switch (name) {
        case "Mittagskarte":
            return {
                label: `${season.lunchDays} · 11:30 – 14:00`,
                text: `${season.lunchDaysLong} von 11:30 bis 14:00 Uhr. ${SIDE_DISH_NOTE}`,
            };
        case "Weinkarte":
            return {
                label: "Wein & Ouzo",
                text: "Unsere Weine sind sorgfältig ausgewählt und stammen von ausgewählten Winzern.",
            };
        default:
            return { label: null, text: SIDE_DISH_NOTE };
    }
};

const euro = (amount) => amount.toFixed(2).replace(".", ",");

const slug = (text) =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9äöüß]+/g, "-")
        .replace(/^-|-$/g, "");

const scrollToCategory = (id) => {
    const el = document.getElementById(id);
    if (el) {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 200 });
        el.focus({ preventScroll: true });
    }
};

const MenuItemRow = ({ item }) => (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-8 border-b border-forest/10 py-5">
        <div>
            <div className="text-[19px]">
                {item.title}
                {item.vegetarian && <VegetarianMark />}
            </div>
            {item.description && (
                <div className="mt-2 max-w-[560px] text-[15px] leading-[1.55] text-forest/70">{item.description}</div>
            )}
        </div>
        <div className="pt-0.5 text-right text-[17px] tabular-nums">
            {item.prices.map((price, index) => (
                <div key={index}>
                    {price.description && price.description.trim() && (
                        <span className="mr-3 text-sm text-forest/70">{price.description}</span>
                    )}
                    {euro(price.amount)}
                </div>
            ))}
        </div>
    </div>
);

// Lädt eine Karte aus der API und gruppiert die Gerichte nach Kategorie.
const useMenu = (name) => {
    const [state, setState] = useState({ status: "loading", groups: [] });

    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading", groups: [] });
        fetch(`/api/public/menus/name/${encodeURIComponent(name)}`)
            .then(async (response) => {
                const data = await response.json();
                if (cancelled) return;
                if (!response.ok) {
                    setState({ status: "missing", groups: [] });
                    return;
                }
                const groups = data.categories
                    .map((category) => ({
                        ...category,
                        id: `kat-${slug(category.title)}`,
                        items: data.items.filter((item) => item.category === category._id),
                    }))
                    .filter((group) => group.items.length > 0);
                setState({ status: groups.length ? "ready" : "missing", groups });
            })
            .catch(() => !cancelled && setState({ status: "error", groups: [] }));
        return () => {
            cancelled = true;
        };
    }, [name]);

    return state;
};

const MenuUnavailable = () => (
    <p className="body-text">
        Diese Karte ist gerade nicht verfügbar. Fragen Sie uns gern telefonisch unter{" "}
        <a href={CONTACT.phoneHref} className="underline">
            {CONTACT.phone}
        </a>
        .
    </p>
);

const TabPanel = ({ tab }) => {
    const intro = tabIntro(tab.name);
    const { status, groups } = useMenu(tab.name);

    return (
        <section className="sec grid-2 pt-24 pb-[88px] desk:pb-[120px]">
            <div className="sticky-label desk:!top-[200px] text-sm leading-[2.1] text-forest/70">
                {groups.length > 1 ? (
                    <nav aria-label="Kategorien" className="flex flex-wrap gap-x-[18px] gap-y-1 desk:block">
                        {groups.map((group) => (
                            <div key={group.id}>
                                <a
                                    href={`#${group.id}`}
                                    className="no-underline"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        scrollToCategory(group.id);
                                    }}
                                >
                                    {group.title}
                                </a>
                            </div>
                        ))}
                    </nav>
                ) : (
                    intro.label && <div className="eyebrow">{intro.label}</div>
                )}
            </div>
            <div className="grid max-w-[860px] wide:max-w-none gap-24">
                {status === "loading" && <p className="text-forest/70">Karte wird geladen …</p>}
                {(status === "missing" || status === "error") && <MenuUnavailable />}
                {status === "ready" &&
                    groups.map((group) => (
                        <div key={group.id} id={group.id} tabIndex={-1} className="reveal scroll-mt-[200px] outline-none">
                            <h2 className="h2-list m-0 mb-2 border-b border-forest pb-4">{group.title}</h2>
                            {/* Breite Schirme: Gerichte zweispaltig */}
                            <div className="wide:grid wide:grid-cols-2 wide:gap-x-16">
                                {group.items.map((item) => (
                                    <MenuItemRow key={item._id} item={item} />
                                ))}
                            </div>
                        </div>
                    ))}
                <div className="max-w-[560px] text-sm leading-[1.7] text-forest/70">
                    {intro.text} Alle Preise in Euro inkl. MwSt. Allergene und Zusatzstoffe nennen wir Ihnen gern am
                    Tisch.
                    {tab.pdf && (
                        <div className="mt-6">
                            <a
                                href={`/documents/${tab.pdf}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-secondary px-6 py-3.5"
                            >
                                {tab.name} als PDF herunterladen
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const TakeawayPanel = () => (
    <section className="sec grid-2 pt-24 pb-[88px] desk:pb-[120px]">
        <div className="eyebrow sticky-label desk:!top-[180px]">Zum Liefern und Mitnehmen</div>
        <div className="max-w-[560px]">
            <h2 className="h2-list m-0 mb-4">Bestellkarte</h2>
            <p className="body-text mb-8">
                Online bestellen zum Abholen oder Liefern, Bezahlung vor Ort. Telefonisch unter{" "}
                <a href={CONTACT.phoneHref} className="underline">
                    {CONTACT.phone}
                </a>
                .
            </p>
            <Link to="/bestellen" className="btn btn-primary">
                Online bestellen
            </Link>
        </div>
    </section>
);

const Karte = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const tabsRef = useRef(null);

    // Reiterwechsel: Ist die Leiste schon nach oben geklebt, an den Anfang der neuen Karte springen,
    // sonst landet man mitten in der neuen (oft kürzeren) Karte.
    const selectTab = (name) => {
        const container = tabsRef.current;
        const start = container ? container.getBoundingClientRect().top + window.scrollY : 0;
        const headerHeight = document.querySelector("header")?.offsetHeight || 0;
        if (window.scrollY > start - headerHeight) {
            window.scrollTo({ top: start - headerHeight, behavior: "instant" });
        }
        navigate(`/karte?${name}`, { replace: true });
    };
    // Alte Links: /karte?Mittagskarte
    const requested = decodeURIComponent(search.replace(/^\?/, ""));
    const active = TABS.find((tab) => tab.name === requested) || TABS[0];

    return (
        <>
            <PageHead
                eyebrow="Karten"
                title={active.name}
                lead={`Griechische und deutsche Küche, saisonal und mediterran. ${getSeason().lunchDaysLong} mittags zusätzlich unsere Mittagskarte.`}
            />
            <div ref={tabsRef} className="relative">
                <div
                    role="tablist"
                    aria-label="Karten"
                    className="sec below-header z-10 flex gap-5 desk:gap-8 overflow-x-auto border-y border-forest/10 bg-creme [scrollbar-width:none]"
                >
                    {TABS.map((tab) => {
                        const selected = tab.name === active.name;
                        return (
                            <button
                                key={tab.name}
                                role="tab"
                                type="button"
                                aria-selected={selected}
                                onClick={() => selectTab(tab.name)}
                                className={`flex-none border-b-2 bg-transparent py-[18px] text-[13px] font-medium uppercase tracking-[.08em] ${
                                    selected ? "border-forest text-forest" : "border-transparent text-forest/70"
                                }`}
                            >
                                {tab.name}
                            </button>
                        );
                    })}
                </div>
                <div role="tabpanel" aria-label={active.name}>
                    {active.name === "Bestellkarte" ? <TakeawayPanel /> : <TabPanel key={active.name} tab={active} />}
                </div>
            </div>
        </>
    );
};

export default Karte;
